import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {Checkbox} from '@/components/ui/checkbox'
import {Input} from '@/components/ui/input'
import {Skeleton} from '@/components/ui/skeleton'
import {Campo, ChipOpcion} from '@/components/campo'
import {BloqueInterno} from '@/components/investigacion/bloque-interno'
import {AfectadoQuickCreateSlot} from '@/components/afectado-quick-create-slot'
import {EmployeeSingleSelect} from '@/components/employee-single-select'
import {useAllEmployees, type Employee} from '@/hooks/empresa/identidad/use-employees'
import {
  useAddAfectado,
  useLesionesCatalog,
  usePatchAfectado,
  useRemoveAfectado,
  type Afectado,
  type AfectadoGravedad,
} from '@/hooks/sgsst/incidentes/use-incidentes'
import {translateApiError} from '@/lib/errors'
import {tope} from '@/lib/campos-texto'

const GRAVEDADES: AfectadoGravedad[] = ['leve', 'grave', 'mortal']
const GRAVEDAD_TONE: Record<AfectadoGravedad, 'neutral' | 'amber' | 'red'> = {
  leve: 'neutral',
  grave: 'amber',
  mortal: 'red',
}
const GRAVEDAD_CHIP: Record<AfectadoGravedad, string | undefined> = {
  leve: undefined,
  grave: 'tone-amber',
  mortal: 'tone-red',
}

function LesionesPicker({
  businessId,
  selected,
  onChange,
}: {
  businessId: string
  selected: string[]
  onChange: (codes: string[]) => void
}) {
  const {t} = useTranslation()
  const {data: catalog} = useLesionesCatalog(businessId)
  function toggle(code: string) {
    onChange(selected.includes(code) ? selected.filter((c) => c !== code) : [...selected, code])
  }
  return (
    <div className="space-y-1.5" data-testid="afectado-lesiones-picker">
      <p className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
        {t('incidentes.afectados.lesionesLabel')}
      </p>
      <ul className="max-h-40 space-y-0.5 overflow-y-auto rounded-lg border border-input bg-card p-1.5">
        {(catalog ?? []).map((item) => (
          <li key={item.code}>
            <label
              data-testid={`afectado-lesion-${item.code}`}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-secondary">
              <Checkbox checked={selected.includes(item.code)} onCheckedChange={() => toggle(item.code)} />
              <span className="min-w-0 truncate">{item.label_es}</span>
            </label>
          </li>
        ))}
        {(catalog ?? []).length === 0 && (
          <li className="px-2 py-1.5 text-xs text-muted-foreground">{t('incidentes.afectados.lesionesVacio')}</li>
        )}
      </ul>
    </div>
  )
}

function AfectadoRow({
  businessId,
  incidenteId,
  afectado,
  lesionLabels,
  canRemove,
  readOnly,
  esPrincipal,
}: {
  businessId: string
  incidenteId: string
  afectado: Afectado
  lesionLabels: Map<string, string>
  canRemove: boolean
  readOnly: boolean
  esPrincipal: boolean
}) {
  const {t} = useTranslation()
  const [editing, setEditing] = useState(false)
  const [gravedad, setGravedad] = useState<AfectadoGravedad>(afectado.gravedad)
  const [lesiones, setLesiones] = useState<string[]>(afectado.tipo_lesion_codes)
  const [dias, setDias] = useState(afectado.dias_incapacidad?.toString() ?? '')
  const [fecha, setFecha] = useState(afectado.dias_incapacidad_certificado_fecha ?? '')
  const [error, setError] = useState<string | null>(null)
  const patch = usePatchAfectado(businessId, incidenteId)
  const remove = useRemoveAfectado(businessId, incidenteId)

  function save() {
    setError(null)
    patch.mutate(
      {
        afectadoId: afectado.id,
        patch: {
          gravedad,
          tipo_lesion_codes: lesiones,
          dias_incapacidad: dias.trim() ? Number(dias) : null,
          dias_incapacidad_certificado_fecha: fecha.trim() ? fecha : null,
        },
      },
      {onSuccess: () => setEditing(false), onError: (err) => setError(translateApiError(err))}
    )
  }

  return (
    <li
      className="space-y-2 rounded-lg border border-l-[3px] border-border border-l-primary p-3"
      data-testid="afectado-row">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
            {afectado.nombre ?? afectado.externo_nombre ?? t('incidentes.afectados.sinNombre')}
            {esPrincipal && <Tag tone="neutral">{t('incidentes.afectados.principal')}</Tag>}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground" data-testid="afectado-severidad">
            {t('incidentes.afectados.severidad', {dias: afectado.severidad_dias})}
            {' · '}
            {t(`incidentes.afectados.fuente.${afectado.severidad_fuente}`)}
          </p>
          {afectado.severidad_fuente === 'incapacidad' && afectado.severidad_dias_tabla != null && (
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              {t('incidentes.afectados.mayorQueTabla', {dias: afectado.severidad_dias_tabla})}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <Tag tone={GRAVEDAD_TONE[afectado.gravedad]}>{t(`incidentes.gravedad.${afectado.gravedad}`)}</Tag>
          {!readOnly && (
            <Button size="xs" variant="ghost" data-testid="afectado-editar" onClick={() => setEditing((v) => !v)}>
              {t('incidentes.afectados.editar')}
            </Button>
          )}
          {!readOnly && canRemove && (
            <Button
              size="xs"
              variant="ghost"
              data-testid="afectado-quitar"
              disabled={remove.isPending}
              onClick={() => remove.mutate(afectado.id)}>
              {t('incidentes.afectados.quitar')}
            </Button>
          )}
        </div>
      </div>
      {afectado.tipo_lesion_codes.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {afectado.tipo_lesion_codes.map((code) => (
            <Tag key={code} tone="neutral">
              {lesionLabels.get(code) ?? code}
            </Tag>
          ))}
        </div>
      )}
      {editing && (
        <div className="space-y-2 border-t border-border pt-2">
          <div className="flex gap-1.5" role="group" aria-label={t('incidentes.ficha.gravedad')}>
            {GRAVEDADES.map((g) => (
              <ChipOpcion
                key={g}
                data-testid={`afectado-gravedad-${g}`}
                seleccionado={gravedad === g}
                tone={GRAVEDAD_CHIP[g]}
                onClick={() => setGravedad(g)}
                className="min-h-10 flex-1 text-xs">
                {t(`incidentes.gravedad.${g}`)}
              </ChipOpcion>
            ))}
          </div>
          <LesionesPicker businessId={businessId} selected={lesiones} onChange={setLesiones} />
          <Campo
            label={t('incidentes.afectados.diasLabel')}
            hint={t('incidentes.afectados.diasHint')}
            htmlFor={`afectado-dias-${afectado.id}`}>
            <Input
              id={`afectado-dias-${afectado.id}`}
              type="number"
              min={0}
              value={dias}
              data-testid="afectado-dias"
              onChange={(e) => setDias(e.target.value)}
              className="h-11 font-mono tabular-nums sm:h-9"
            />
          </Campo>
          <Campo label={t('incidentes.afectados.fechaLabel')} htmlFor={`afectado-fecha-${afectado.id}`} opcional>
            <Input
              id={`afectado-fecha-${afectado.id}`}
              type="date"
              value={fecha}
              data-testid="afectado-fecha"
              onChange={(e) => setFecha(e.target.value)}
              className="h-11 font-mono tabular-nums sm:h-9"
            />
          </Campo>
          <Button size="sm" data-testid="afectado-guardar" disabled={patch.isPending} onClick={save}>
            {t('incidentes.afectados.guardar')}
          </Button>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      )}
    </li>
  )
}

type NuevoModo = 'empleado' | 'externo'

function NuevoAfectado({
  businessId,
  incidenteId,
  afectados,
  principalEmployeeId,
}: {
  businessId: string
  incidenteId: string
  // Se excluyen del selector: el 409 del servidor es la red de seguridad, no el camino.
  afectados: Afectado[]
  principalEmployeeId: string | null
}) {
  const {t} = useTranslation()
  // Sin roster el dedup por cédula dejaría pasar duplicados en silencio: por eso se mira el error.
  const {data: roster, isError: rosterDenegado} = useAllEmployees(businessId)
  const add = useAddAfectado(businessId, incidenteId)
  const [modo, setModo] = useState<NuevoModo>('empleado')
  const [empleado, setEmpleado] = useState<Employee | null>(null)
  const [externoNombre, setExternoNombre] = useState('')
  const [externoDocumento, setExternoDocumento] = useState('')
  const [gravedad, setGravedad] = useState<AfectadoGravedad>('leve')
  const [lesiones, setLesiones] = useState<string[]>([])
  const [dias, setDias] = useState('')
  const [fecha, setFecha] = useState('')
  const [error, setError] = useState<string | null>(null)

  const valido = modo === 'empleado' ? empleado !== null : externoNombre.trim().length > 0
  const yaAfectados = new Set(afectados.map((a) => a.employee_id).filter((id): id is string => id !== null))

  function submit() {
    setError(null)
    add.mutate(
      {
        employee_id: modo === 'empleado' ? empleado!.id : null,
        externo_nombre: modo === 'externo' ? externoNombre.trim() : null,
        externo_cedula: modo === 'externo' && externoDocumento.trim() ? externoDocumento.trim() : null,
        gravedad,
        tipo_lesion_codes: lesiones,
        dias_incapacidad: dias.trim() ? Number(dias) : null,
        dias_incapacidad_certificado_fecha: fecha.trim() ? fecha : null,
      },
      {
        onSuccess: () => {
          setEmpleado(null)
          setExternoNombre('')
          setExternoDocumento('')
          setGravedad('leve')
          setLesiones([])
          setDias('')
          setFecha('')
        },
        onError: (err) => setError(translateApiError(err)),
      }
    )
  }

  return (
    <BloqueInterno titulo={t('incidentes.afectados.agregar')}>
      <Campo label={t('incidentes.reportar.quienLabel')}>
        <div className="grid grid-cols-2 gap-2" role="group" aria-label={t('incidentes.reportar.quienLabel')}>
          {(['empleado', 'externo'] as NuevoModo[]).map((v) => (
            <ChipOpcion
              key={v}
              data-testid={`afectado-nuevo-modo-${v}`}
              seleccionado={modo === v}
              onClick={() => setModo(v)}>
              {t(`incidentes.reportar.afectadoModo.${v}`)}
            </ChipOpcion>
          ))}
        </div>
        {modo === 'empleado' ? (
          <>
            <EmployeeSingleSelect
              roster={roster ?? []}
              value={empleado}
              onChange={setEmpleado}
              exclude={(e) => e.id === principalEmployeeId || yaAfectados.has(e.id)}
              testId="afectado-nuevo-empleado"
            />
            {/* Un accidente no se frena porque un afectado no esté en la nómina (igual que el wizard de reportar). */}
            <AfectadoQuickCreateSlot
              businessId={businessId}
              roster={roster ?? []}
              rosterDenegado={rosterDenegado}
              oculto={!!empleado}
              onCreated={setEmpleado}
            />
          </>
        ) : (
          <div className="space-y-2">
            <Input
              maxLength={tope('NOM')}
              type="text"
              value={externoNombre}
              data-testid="afectado-nuevo-externo"
              onChange={(e) => setExternoNombre(e.target.value)}
              placeholder={t('incidentes.reportar.afectadoExternoPlaceholder')}
              className="h-11 sm:h-9"
            />
            {/* Opcional y sin validar formato a propósito: un extranjero llega con pasaporte y solo desempata homónimos. */}
            <Campo
              label={t('incidentes.afectados.documentoLabel')}
              hint={t('incidentes.afectados.documentoHint')}
              htmlFor="afectado-nuevo-externo-documento"
              opcional>
              <Input
                maxLength={tope('DOC')}
                id="afectado-nuevo-externo-documento"
                type="text"
                value={externoDocumento}
                data-testid="afectado-nuevo-externo-documento"
                onChange={(e) => setExternoDocumento(e.target.value)}
                className="h-11 font-mono sm:h-9"
              />
            </Campo>
          </div>
        )}
      </Campo>
      <Campo label={t('incidentes.ficha.gravedad')}>
        <div className="flex gap-1.5" role="group" aria-label={t('incidentes.ficha.gravedad')}>
          {GRAVEDADES.map((g) => (
            <ChipOpcion
              key={g}
              data-testid={`afectado-nuevo-gravedad-${g}`}
              seleccionado={gravedad === g}
              tone={GRAVEDAD_CHIP[g]}
              onClick={() => setGravedad(g)}
              className="min-h-10 flex-1 text-xs">
              {t(`incidentes.gravedad.${g}`)}
            </ChipOpcion>
          ))}
        </div>
      </Campo>
      <LesionesPicker businessId={businessId} selected={lesiones} onChange={setLesiones} />
      <Campo
        label={t('incidentes.afectados.diasLabel')}
        hint={t('incidentes.afectados.diasHint')}
        htmlFor="afectado-nuevo-dias">
        <Input
          id="afectado-nuevo-dias"
          type="number"
          min={0}
          value={dias}
          data-testid="afectado-nuevo-dias"
          onChange={(e) => setDias(e.target.value)}
          className="h-11 font-mono tabular-nums sm:h-9"
        />
      </Campo>
      <Campo label={t('incidentes.afectados.fechaLabel')} htmlFor="afectado-nuevo-fecha" opcional>
        <Input
          id="afectado-nuevo-fecha"
          type="date"
          value={fecha}
          data-testid="afectado-nuevo-fecha"
          onChange={(e) => setFecha(e.target.value)}
          className="h-11 font-mono tabular-nums sm:h-9"
        />
      </Campo>
      <Button
        size="sm"
        className="min-h-11 sm:min-h-0"
        data-testid="afectado-nuevo-submit"
        disabled={!valido || add.isPending}
        onClick={submit}>
        {t('incidentes.afectados.agregarSubmit')}
      </Button>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </BloqueInterno>
  )
}

/** El afectado del evento leído del incidente: cubre cuando la investigación no carga o no se puede leer. */
function PrincipalFallback({nombre}: {nombre: string}) {
  const {t} = useTranslation()
  return (
    <li
      className="space-y-1 rounded-lg border border-l-[3px] border-border border-l-primary p-3"
      data-testid="afectado-principal-fallback">
      <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
        {nombre}
        <Tag tone="neutral">{t('incidentes.afectados.principal')}</Tag>
      </p>
      <p className="text-xs text-muted-foreground">{t('incidentes.afectados.principalHint')}</p>
    </li>
  )
}

/** El afectado #1 es el del evento y no se puede quitar: un accidente sin afectado es un expediente incompleto. */
export function AfectadosManager({
  businessId,
  incidenteId,
  afectados,
  readOnly,
  isLoading,
  principalNombre,
  principalEmployeeId,
}: {
  businessId: string
  incidenteId: string
  afectados: Afectado[]
  readOnly: boolean
  isLoading: boolean
  principalNombre: string | null
  principalEmployeeId: string | null
}) {
  const {t} = useTranslation()
  const {data: catalog} = useLesionesCatalog(businessId)
  const lesionLabels = new Map((catalog ?? []).map((c) => [c.code, c.label_es]))
  // Un externo se casa por nombre: su `afectado_nombre` es el `externo_nombre` sembrado.
  const raizIndex = afectados.findIndex((a) =>
    principalEmployeeId !== null
      ? a.employee_id === principalEmployeeId
      : a.employee_id === null && a.externo_nombre === principalNombre
  )
  const faltaElPrincipal = raizIndex === -1 && principalNombre !== null

  if (isLoading) return <Skeleton className="h-20 w-full" data-testid="afectados-cargando" />

  return (
    <div className="space-y-3" data-testid="afectados-manager">
      <ul className="space-y-2">
        {faltaElPrincipal && <PrincipalFallback nombre={principalNombre} />}
        {afectados.map((a, index) => (
          <AfectadoRow
            key={a.id}
            businessId={businessId}
            incidenteId={incidenteId}
            afectado={a}
            lesionLabels={lesionLabels}
            canRemove={index !== raizIndex}
            readOnly={readOnly}
            esPrincipal={index === raizIndex}
          />
        ))}
        {afectados.length === 0 && !faltaElPrincipal && (
          <p className="text-sm text-muted-foreground">{t('incidentes.afectados.vacio')}</p>
        )}
      </ul>
      {!readOnly && (
        <NuevoAfectado
          businessId={businessId}
          incidenteId={incidenteId}
          afectados={afectados}
          principalEmployeeId={principalEmployeeId}
        />
      )}
    </div>
  )
}
